/** The welcome title on every other day: the only source copy of it. */
export const WELCOME_TITLE = 'What can we work on?';
/** Code points, as gyro-svc's sanitizeDayGreeting allows. */
export const DAY_GREETING_MAX = 140;
/** Code points: past this the title steps down a size so a long greeting still fits the welcome. */
export const DAY_GREETING_LONG = 40;
/** A failed re-read of /v1/status waits this long before the next try. */
export const DAY_REFRESH_RETRY_MS = 60_000;
/**
 * What a greeting may never hold (gyro-svc's GREETING_REFUSED, desktopMascotDays.ts): C0 and C1 controls (newlines and
 * tabs included), DEL, the line and paragraph separators, bidirectional controls, and invisible characters that are not
 * part of an emoji (soft hyphen, Mongolian vowel separator, zero-width space, word joiner, invisible operators, byte order
 * mark, interlinear annotation marks). The zero-width joiner is allowed, so a joined emoji stays one emoji.
 */
const REFUSED = /[\u0000-\u001f\u007f-\u009f\u00ad\u061c\u180e\u200b\u200e\u200f\u2028\u2029\u202a-\u202e\u2060-\u2064\u2066-\u2069\ufeff\ufff9-\ufffb]/;
/** Tag characters are invisible: allowed only inside a flag emoji's tag sequence. */
const TAG_CHARACTER = /[\u{E0000}-\u{E007F}]/u;
const FLAG_TAG_SEQUENCE = /\u{1F3F4}[\u{E0020}-\u{E007E}]+\u{E007F}/gu;
const LONE_SURROGATE = /[\ud800-\udbff](?![\udc00-\udfff])|(?<![\ud800-\udbff])[\udc00-\udfff]/;
const codePoints = (text) => Array.from(text).length;
/**
 * A greeting fit to show: NFC, runs of white space made one space, trimmed, 1 to 140 code points of well-formed text
 * with none of the refused characters; null for anything else. Markdown is kept as it is (it is shown as plain text).
 */
export function dayGreetingText(value) {
    if (typeof value !== 'string' || value.length > 8 * DAY_GREETING_MAX)
        return null;
    if (LONE_SURROGATE.test(value) || REFUSED.test(value) || TAG_CHARACTER.test(value.replace(FLAG_TAG_SEQUENCE, '')))
        return null;
    const text = value.normalize('NFC').replace(/\s+/g, ' ').trim();
    const length = codePoints(text);
    return length >= 1 && length <= DAY_GREETING_MAX ? text : null;
}
/** A greeting and when it ends (epoch ms, a finite number above 0), or null when either is missing or malformed. */
export function parseDayGreeting(greeting, untilMs) {
    const text = dayGreetingText(greeting);
    if (text === null || typeof untilMs !== 'number' || !Number.isFinite(untilMs) || untilMs <= 0)
        return null;
    return { greeting: text, untilMs };
}
/** /v1/status's persona: its `dayGreeting` and `dayGreetingUntilMs`, or null (the usual title). */
export function dayGreetingFromPersona(persona) {
    if (!persona || typeof persona !== 'object' || Array.isArray(persona))
        return null;
    const record = persona;
    return parseDayGreeting(record.dayGreeting, record.dayGreetingUntilMs);
}
/**
 * The welcome title: the day's greeting while it lasts (it wins on its day), else the usual title. Nothing else ever takes
 * the title: the "Your PPP is due <weekday>" line that used to stand here was removed on the owner's word (2026-10-19,
 * "i do not want this to have a reminder").
 */
export function welcomeHeading(day, nowMs) {
    if (day && nowMs < day.untilMs)
        return { text: day.greeting, day: codePoints(day.greeting) > DAY_GREETING_LONG ? 'long' : 'short' };
    return { text: WELCOME_TITLE, day: null };
}
/**
 * Whether /v1/status should be read again for the day's greeting: never before a first answer (the status check owns
 * that one); when the greeting shown has ended; or when the local date has changed since the last answer.
 */
export function dayStatusDue(day, lastAnswerAtMs, nowMs) {
    if (lastAnswerAtMs === null)
        return false;
    if (day && nowMs >= day.untilMs)
        return true;
    return new Date(lastAnswerAtMs).toDateString() !== new Date(nowMs).toDateString();
}
export function sameDayGreeting(a, b) {
    if (a === null || b === null)
        return a === b;
    return a.greeting === b.greeting && a.untilMs === b.untilMs;
}
//# sourceMappingURL=dayGreeting.js.map